import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tag } from "primereact/tag";

import {
  generateExplanationsForTestItem,
  importFromJsonText,
  importFromPdfs,
} from "./adminLogic.js";
import {
  deleteTestExplanationsFromApi,
  deleteTestFromApi,
  fetchAdminExplanationCounts,
  fetchAdminTestFull,
  fetchAdminExplanationsBulk,
  fetchAccountDeletionRequests,
  deleteUser,
  fetchAdminTestsCatalog,
  fetchAdminTestsExport,
  fetchAdminTestsAttemptStats,
  getAdminStats,
  getAdminUser,
  getAdminUsers,
  getUserAuditLog,
  getUserAttempts,
  login,
  publishTestsToApi,
  approveAccountDeletionRequest,
  createAdminUser,
  executeAccountDeletionRequest,
  rejectAccountDeletionRequest,
  resetUserPassword,
  setUserPlan,
  setUserSuspended,
  verifyUserAccount
} from "./api.js";

function canExplain(tit) {
  return ["PER", "PER_PNB", "PNB"].includes(tit);
}

const SESSION_KEY = "nautica_admin_session_v1";

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleString();
  } catch {
    return String(s);
  }
}

function bucketToMap(arr) {
  const m = {};
  for (const x of Array.isArray(arr) ? arr : []) m[String(x && x._id)] = Number(x && x.count) || 0;
  return m;
}

function auditActionLabel(action) {
  const labels = {
    account_verify: "Validar conta",
    account_suspend: "Suspender",
    account_unsuspend: "Reativar",
    account_reset_password: "Reset password",
    account_soft_delete: "Apagar conta (soft delete)"
  };
  return labels[action] || String(action || "—");
}

function formatAuditActor(actor) {
  if (!actor || typeof actor !== "object") return "—";
  if (actor.kind === "session") return String(actor.email || actor.userId || "?");
  if (actor.kind === "admin_token") return "ADMIN_TOKEN (x-admin-token)";
  return String(actor.kind || "—");
}

function summarizeAuditDetails(details) {
  if (!details || typeof details !== "object") return "—";
  try {
    const s = JSON.stringify(details);
    return s.length > 140 ? s.slice(0, 137) + "…" : s;
  } catch {
    return "—";
  }
}

function modeLabel(m) {
  return m === "exam" ? "Exame" : "Assistido";
}

function attemptRowKey(a, idx) {
  return `${a.ts || ""}|${a.testId || ""}|${idx}`;
}

function storageLabel(meta) {
  const s = meta && meta.stored;
  if (s === "full") return "Completo";
  if (s === "shrunk") return "Compactado";
  if (s === "partial") return "Parcial";
  return "—";
}

function safeJsonStringify(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

export function App() {
  const initialSession = safeJsonParse(localStorage.getItem(SESSION_KEY) || "null", null);
  const [session, setSession] = useState(initialSession && initialSession.token ? initialSession : null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [tab, setTab] = useState("import"); // "import" | "users" | "deletions"

  const [month, setMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());

  const [status, setStatus] = useState("Listo");
  const [apiStatus, setApiStatus] = useState("");
  const [logText, setLogText] = useState("");

  const [jsonIn, setJsonIn] = useState("");
  const [itemsTick, setItemsTick] = useState(0);
  const [replaceAllOnPublish, setReplaceAllOnPublish] = useState(true);
  /** IDs de testes que existem na coleção `tests` da API. */
  const [publishedOnApi, setPublishedOnApi] = useState(() => new Set());
  const [publishedSyncLoading, setPublishedSyncLoading] = useState(false);
  const [publishedSyncError, setPublishedSyncError] = useState("");
  /** Catálogo vindo do MongoDB (todas as versões). */
  const [apiCatalog, setApiCatalog] = useState([]);
  /** `id` do teste enquanto há operação em curso. */
  const [perTestApiBusyId, setPerTestApiBusyId] = useState(null);
  /** Expansão da tabela de testes (`familyKey` → true), só uma família aberta. */
  const [importedTableExpandedRows, setImportedTableExpandedRows] = useState(null);
  const [importedTableGlobalFilter, setImportedTableGlobalFilter] = useState("");
  const [importedTableFilters, setImportedTableFilters] = useState(() => ({
    month: { value: null, matchMode: FilterMatchMode.CONTAINS },
    yearNum: { value: null, matchMode: FilterMatchMode.EQUALS },
    titulation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    testCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
    active: { value: null, matchMode: FilterMatchMode.EQUALS },
    versionCount: { value: null, matchMode: FilterMatchMode.EQUALS }
  }));
  const [testAttemptStatsById, setTestAttemptStatsById] = useState(() => ({}));
  const [testAttemptStatsLoading, setTestAttemptStatsLoading] = useState(false);
  const actionsPanelRef = useRef(null);
  const [actionsTarget, setActionsTarget] = useState(null);
  /** testId → número de documentos de explicação na API (Mongo). */
  const [explCountByTestId, setExplCountByTestId] = useState(() => ({}));

  const pdfEnRef = useRef(null);
  const pdfAnRef = useRef(null);

  // Users area state
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersQuery, setUsersQuery] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("user"); // "user" | "reviewer" | "admin" | "superadmin"
  const [createUserBusy, setCreateUserBusy] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [expandedAttemptKey, setExpandedAttemptKey] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [pwResetInfo, setPwResetInfo] = useState(null); // { userId, email, tempPassword, emailed, at }
  const [verifyInfo, setVerifyInfo] = useState(null); // { userId, email, tempPassword?, emailed, at }

  // Deletions area state
  const [delLoading, setDelLoading] = useState(false);
  const [delStatusFilter, setDelStatusFilter] = useState("pending");
  const [delRequests, setDelRequests] = useState([]);
  const [delQuery, setDelQuery] = useState("");

  const items = useMemo(() => {
    itemsTick;
    return Array.isArray(apiCatalog) ? apiCatalog : [];
  }, [itemsTick]);

  const groupedItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const byFam = new Map();
    for (const it of list) {
      const fk =
        it?.familyKey ||
        `${it?.titulation || ""}|${it?.testCode || ""}|${String(it?.month || "_").trim().toLowerCase() || "_"}|${
          it?.year != null && it?.year !== "" ? String(it?.year) : "_"
        }`;
      if (!byFam.has(fk)) byFam.set(fk, []);
      byFam.get(fk).push(it);
    }
    const groups = [...byFam.entries()].map(([familyKey, versions]) => {
      const vs = [...versions].sort((a, b) => (Number(b?.version) || 0) - (Number(a?.version) || 0));
      const active = vs.find((x) => x?.active === true) || null;
      const latest = vs[0] || null;
      return { familyKey, versions: vs, active, latest };
    });
    // Sort groups: active first, then by year desc, then testCode asc, then familyKey
    groups.sort((a, b) => {
      const aa = a.active ? 1 : 0;
      const bb = b.active ? 1 : 0;
      if (aa !== bb) return bb - aa;
      const ay = Number(a.latest?.year) || 0;
      const by = Number(b.latest?.year) || 0;
      if (ay !== by) return by - ay;
      const ac = String(a.latest?.testCode || "");
      const bc = String(b.latest?.testCode || "");
      const ccmp = ac.localeCompare(bc);
      if (ccmp) return ccmp;
      return String(a.familyKey).localeCompare(String(b.familyKey));
    });
    return groups;
  }, [items]);

  const importedCatalogRows = useMemo(() => {
    return groupedItems.map((g) => {
      const rep = g.active || g.latest || g.versions[0] || {};
      const yRaw = rep.year;
      const yearNum =
        yRaw != null && yRaw !== "" && Number.isFinite(Number(yRaw)) ? Number(yRaw) : null;
      const activeIt = g.active || null;
      const nQuestions = (activeIt?.questions || rep.questions || []).length;
      const explApi = !!session?.token;
      const explForActive = activeIt && explApi ? Number(explCountByTestId[String(activeIt.id)] || 0) : null;
      const explLabel = !explApi
        ? "—"
        : activeIt && nQuestions > 0
          ? `${explForActive}/${nQuestions}`
          : activeIt
            ? "0/0"
            : "—";
      return {
        familyKey: g.familyKey,
        _group: g,
        month: rep.month != null ? String(rep.month) : "",
        yearNum,
        yearDisplay: rep.year != null && rep.year !== "" ? String(rep.year) : "—",
        titulation: String(rep.titulationLabel || rep.titulation || ""),
        testCode: String(rep.testCode || ""),
        active: !!g.active,
        activeLabel: g.active ? `Sí (v${g.active?.version || 1})` : "No",
        versionCount: g.versions.length,
        versionCountLabel: String(g.versions.length),
        explLabel,
        explSearchText: `${explLabel} explicaciones explicações`
      };
    });
  }, [groupedItems, explCountByTestId, session?.token]);

  const importedActiveFilterOptions = useMemo(
    () => [
      { label: "Todos", value: null },
      { label: "Activo", value: true },
      { label: "Inactivo", value: false }
    ],
    []
  );

  const clearImportedTableFilters = useCallback(() => {
    setImportedTableGlobalFilter("");
    setImportedTableFilters({
      month: { value: null, matchMode: FilterMatchMode.CONTAINS },
      yearNum: { value: null, matchMode: FilterMatchMode.EQUALS },
      titulation: { value: null, matchMode: FilterMatchMode.CONTAINS },
      testCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
      active: { value: null, matchMode: FilterMatchMode.EQUALS },
      versionCount: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
  }, []);

  const onImportedTableRowToggle = useCallback((e) => {
    const data = e.data;
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      setImportedTableExpandedRows(null);
      return;
    }
    const keys = Object.keys(data);
    if (!keys.length) {
      setImportedTableExpandedRows(null);
      return;
    }
    setImportedTableExpandedRows((prev) => {
      const prevKeys = prev && typeof prev === "object" && !Array.isArray(prev) ? Object.keys(prev) : [];
      if (keys.length === 1) return { [keys[0]]: true };
      const newly = keys.find((k) => !prevKeys.includes(k));
      return newly ? { [newly]: true } : { [keys[keys.length - 1]]: true };
    });
  }, []);

  const syncPublishedFromApi = useCallback(async () => {
    if (!session?.token) {
      setPublishedSyncError("Inicia sessão para sincronizar o estado na API.");
      setPublishedOnApi(new Set());
      setApiCatalog([]);
      return;
    }
    setPublishedSyncLoading(true);
    setPublishedSyncError("");
    try {
      const catalog = await fetchAdminTestsCatalog({ authToken: session.token });
      setApiCatalog(Array.isArray(catalog) ? catalog : []);
      const ids = new Set((catalog || []).map((t) => String(t && t.id)).filter(Boolean));
      setPublishedOnApi(ids);
      setItemsTick((t) => t + 1);
    } catch (e) {
      setPublishedSyncError(e && e.message ? e.message : String(e));
    } finally {
      setPublishedSyncLoading(false);
    }
  }, [session?.token]);

  async function refreshDeletions() {
    if (!session?.token) return;
    setDelLoading(true);
    try {
      const r = await fetchAccountDeletionRequests({ authToken: session.token, status: delStatusFilter });
      setDelRequests(Array.isArray(r?.requests) ? r.requests : []);
    } catch (e) {
      setDelRequests([]);
      alert("Erro a carregar pedidos de eliminação: " + (e && e.message ? e.message : String(e)));
    } finally {
      setDelLoading(false);
    }
  }

  const ensureAttemptStats = useCallback(
    async (testIds) => {
      if (!session?.token) return;
      const ids = (Array.isArray(testIds) ? testIds : []).map((x) => String(x || "")).filter(Boolean);
      if (!ids.length) return;
      const missing = ids.filter((id) => !testAttemptStatsById[id]);
      if (!missing.length) return;
      setTestAttemptStatsLoading(true);
      try {
        const r = await fetchAdminTestsAttemptStats({ authToken: session.token, testIds: missing });
        const by = (r && r.byTestId) || {};
        setTestAttemptStatsById((prev) => ({ ...prev, ...by }));
      } catch {
        // ignore (stats are optional)
      } finally {
        setTestAttemptStatsLoading(false);
      }
    },
    [session?.token, testAttemptStatsById]
  );

  const onImportedTableRowExpand = useCallback(
    (e) => {
      const g = e.data?._group;
      if (g?.versions?.length) void ensureAttemptStats(g.versions.map((x) => x.id));
    },
    [ensureAttemptStats]
  );

  useEffect(() => {
    if (tab !== "import" || !session?.token) return;
    syncPublishedFromApi();
  }, [tab, session?.token, syncPublishedFromApi]);

  useEffect(() => {
    if (tab !== "import") return;
    if (!session?.token) {
      setExplCountByTestId({});
      return;
    }
    let cancelled = false;
    const ids = (Array.isArray(apiCatalog) ? apiCatalog : []).map((x) => String(x?.id || "")).filter(Boolean);
    if (!ids.length) {
      setExplCountByTestId({});
      return;
    }
    (async () => {
      try {
        const r = await fetchAdminExplanationCounts({ authToken: session.token, testIds: ids });
        const m = (r && r.byTestId) || {};
        if (!cancelled) setExplCountByTestId(m);
      } catch {
        if (!cancelled) setExplCountByTestId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, itemsTick, session?.token]);

  function log(msg) {
    setLogText((t) => (t ? t + "\n" : "") + String(msg));
  }

  function clearLog() {
    setLogText("");
  }

  async function onImportPdfs() {
    const fEn = pdfEnRef.current?.files?.[0] || null;
    const fAn = pdfAnRef.current?.files?.[0] || null;
    if (!fEn || !fAn) {
      alert("Selecciona ambos PDFs.");
      return;
    }

    clearLog();
    try {
      if (!session?.token) throw new Error("Inicia sesión como admin.");
      const imported = await importFromPdfs({
        month: String(month || "").trim(),
        year: year != null && year !== "" && Number.isFinite(Number(year)) && Number(year) > 0 ? Number(year) : null,
        pdfEnunciados: fEn,
        pdfRespuestas: fAn,
        log,
        setStatus
      });
      setStatus("Publicando en API…");
      const r = await publishTestsToApi({
        tests: Array.isArray(imported) ? imported : [],
        explanations: null,
        replaceAll: false,
        authToken: session.token
      });
      setApiStatus(`API OK: ${r.upserted} upsert, ${r.modified} modified`);
      setStatus("Listo");
      await syncPublishedFromApi();
    } catch (e) {
      console.error(e);
      setStatus("Error");
      log(`ERROR: ${e && e.message ? e.message : String(e)}`);
      alert("Error importando: " + (e && e.message ? e.message : String(e)));
    }
  }

  async function onExport() {
    if (!session?.token) {
      alert("Inicia sesión como admin.");
      return;
    }
    const r = await fetchAdminTestsExport({ authToken: session.token });
    const payload = JSON.stringify((r && r.tests) || [], null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      alert("Exportado al portapapeles (JSON).");
    } catch {
      alert(payload);
    }
  }

  async function onPublishApi() {
    setApiStatus("");
    try {
      if (!session?.token) throw new Error("Inicia sesión como admin.");
      const r0 = await fetchAdminTestsExport({ authToken: session.token });
      const payload = Array.isArray(r0?.tests) ? r0.tests : [];
      const ids = payload.map((x) => String(x?.id || "")).filter(Boolean);
      const explanations = await fetchAdminExplanationsBulk({ authToken: session.token, testIds: ids });
      setStatus("Publicando en API…");
      const r = await publishTestsToApi({
        tests: payload,
        explanations,
        replaceAll: replaceAllOnPublish,
        authToken: session.token
      });
      setApiStatus(`API OK: ${r.upserted} upsert, ${r.modified} modified`);
      setStatus("Listo");
      await syncPublishedFromApi();
    } catch (e) {
      setStatus("Error");
      setApiStatus(`API ERROR: ${e && e.message ? e.message : String(e)}`);
      alert("Error publicando en API: " + (e && e.message ? e.message : String(e)));
    }
  }

  async function onLogin() {
    setApiStatus("");
    try {
      const r = await login({ email: loginEmail.trim(), password: loginPassword });
      if (!r?.token || !r?.user) throw new Error("Login inválido.");
      if (r.user.role !== "admin" && r.user.role !== "superadmin") throw new Error("Este utilizador não tem permissões de admin.");
      const next = { token: r.token, user: r.user };
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      setSession(next);
      setLoginPassword("");
      setApiStatus(`Sessão iniciada: ${r.user.email}`);
    } catch (e) {
      setApiStatus(`LOGIN ERROR: ${e && e.message ? e.message : String(e)}`);
      alert("Erro no login: " + (e && e.message ? e.message : String(e)));
    }
  }

  function onLogout() {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setExplCountByTestId({});
    setApiStatus("Sessão terminada.");
  }

  async function onWipe() {
    try {
      if (!session?.token) throw new Error("Inicia sesión como admin.");
      const ok = window.confirm("APAGAR TODOS os testes e explicações no MongoDB? (replaceAll)");
      if (!ok) return;
      setStatus("A apagar na API…");
      await publishTestsToApi({ tests: [], explanations: null, replaceAll: true, authToken: session.token });
      setStatus("Listo");
      await syncPublishedFromApi();
    } catch (e) {
      alert("Erro ao limpar: " + (e && e.message ? e.message : String(e)));
    }
  }

  async function onImportJson() {
    try {
      if (!session?.token) throw new Error("Inicia sesión como admin.");
      const arr = importFromJsonText({ jsonText: jsonIn });
      setStatus("Publicando en API…");
      const r = await publishTestsToApi({ tests: Array.isArray(arr) ? arr : [], replaceAll: false, authToken: session.token });
      setApiStatus(`API OK: ${r.upserted} upsert, ${r.modified} modified`);
      setStatus("Listo");
      setJsonIn("");
      await syncPublishedFromApi();
    } catch (e) {
      alert("Error importando JSON: " + (e && e.message ? e.message : String(e)));
    }
  }

  async function onExplain(id) {
    if (!session?.token) {
      alert("Inicia sessão como admin para gerar explicações: o temário vem da API (já não há PDFs públicos no site admin).");
      return;
    }
    clearLog();
    try {
      // MongoDB é a única source of truth: buscar o teste completo do servidor.
      const full = await fetchAdminTestFull({ authToken: session.token, testId: id });
      await generateExplanationsForTestItem({ testItem: full, log, setStatus, authToken: session.token });
      setItemsTick((t) => t + 1);
      try {
        const r = await fetchAdminExplanationCounts({ authToken: session.token, testIds: [id] });
        const m = (r && r.byTestId) || {};
        setExplCountByTestId((prev) => ({ ...prev, ...m }));
      } catch {
        /* ignore */
      }
    } catch (e) {
      console.error(e);
      setStatus("Error");
      log(`ERROR explicaciones: ${e && e.message ? e.message : String(e)}`);
      alert("Error generando explicaciones: " + (e && e.message ? e.message : String(e)));
    }
  }

  async function onRemove(id) {
    const it = (Array.isArray(items) ? items : []).find((x) => x.id === id);
    if (!it) return;
    setPerTestApiBusyId(id);
    try {
      if (!session?.token) throw new Error("Inicia sessão para despublicar.");
      await deleteTestFromApi({ testId: id, authToken: session.token });
      await syncPublishedFromApi();
    } finally {
      setPerTestApiBusyId(null);
    }
  }

  async function onClearApiExplanations(it) {
    if (!it?.id || !session?.token) return;
    const ok = window.confirm(
      "Apagar todas as explicações deste teste no MongoDB? Quem pratica deixa de as ver; o documento do teste (se existir) mantém-se."
    );
    if (!ok) return;
    setPerTestApiBusyId(it.id);
    try {
      const r = await deleteTestExplanationsFromApi({ testId: it.id, authToken: session.token });
      alert(`Removidas na API: ${r.explanationsDeleted ?? 0} explicações.`);
      try {
        const cr = await fetchAdminExplanationCounts({ authToken: session.token, testIds: [it.id] });
        const m = (cr && cr.byTestId) || {};
        setExplCountByTestId((prev) => ({ ...prev, ...m }));
      } catch {
        /* ignore */
      }
    } catch (e) {
      alert("Erro: " + (e && e.message ? e.message : String(e)));
    } finally {
      setPerTestApiBusyId(null);
    }
  }

  function openActionsMenu(e, it) {
    e.preventDefault();
    e.stopPropagation();
    setActionsTarget(it);
    actionsPanelRef.current?.toggle?.(e);
  }

  function renderImportedTestExpansion(rowData) {
    const g = rowData?._group;
    if (!g) return null;
    return (
      <div style={{ padding: "8px 10px 12px" }}>
        <div className="small" style={{ marginBottom: 6 }}>
          <strong>Versões</strong>
        </div>
        <div className="imported-tests-nested-scroll">
          <table className="simpleTable" style={{ marginTop: 0, minWidth: "520px" }}>
            <thead>
              <tr>
                <th>Ver.</th>
                <th>Activo</th>
                <th>Perg.</th>
                <th>Explic.</th>
                <th>API</th>
                <th>Utilizadores</th>
                <th style={{ width: "3rem" }} />
              </tr>
            </thead>
            <tbody>
              {g.versions.map((it) => {
                const st = testAttemptStatsById[String(it.id)] || null;
                const nQv = (it.questions || []).length;
                const nEv = session?.token ? Number(explCountByTestId[String(it.id)] || 0) : null;
                const explFull = nEv != null && nQv > 0 && nEv >= nQv;
                const explPartial = nEv != null && nEv > 0 && !explFull;
                return (
                  <tr key={it.id}>
                    <td className="small">{it.version != null ? String(it.version) : "1"}</td>
                    <td className="small">
                      {it.active === true ? <Tag value="Sí" severity="success" /> : <Tag value="No" severity="secondary" />}
                    </td>
                    <td className="small">{String(nQv)}</td>
                    <td className="small" title="Explicações na API (Mongo)">
                      {nEv == null ? (
                        <span className="small">—</span>
                      ) : explFull ? (
                        <Tag value={`${nEv}/${nQv}`} severity="success" />
                      ) : explPartial ? (
                        <Tag value={`${nEv}/${nQv}`} severity="warning" />
                      ) : (
                        <Tag value={nQv ? `0/${nQv}` : "0"} severity="secondary" />
                      )}
                    </td>
                    <td className="small">
                      {publishedSyncLoading ? (
                        "…"
                      ) : publishedOnApi.has(String(it.id)) ? (
                        <Tag value="Sim" severity="success" />
                      ) : (
                        <Tag value="Não" severity="secondary" />
                      )}
                    </td>
                    <td className="small" style={{ maxWidth: "14rem" }}>
                      {st ? (
                        <div
                          className="small"
                          style={{ lineHeight: 1.45, whiteSpace: "normal" }}
                          title={`${st.completedUsers} completaram · ${st.failedUsers} suspensos · ${st.passedUsers} passaram`}
                        >
                          compl.: {st.completedUsers} · susp.: {st.failedUsers} · pass.: {st.passedUsers}
                        </div>
                      ) : testAttemptStatsLoading ? (
                        "…"
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Button
                        icon="pi pi-cog"
                        className="p-button-sm p-button-secondary"
                        aria-label="Ações"
                        tooltip="Ações"
                        tooltipOptions={{ position: "top" }}
                        disabled={!!perTestApiBusyId || publishedSyncLoading}
                        onClick={(e) => openActionsMenu(e, it)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  async function refreshUsers() {
    if (!session?.token) return;
    setUsersLoading(true);
    try {
      const list = await getAdminUsers({ authToken: session.token });
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      alert("Erro a carregar utilizadores: " + (e && e.message ? e.message : String(e)));
    } finally {
      setUsersLoading(false);
    }
  }

  async function onCreateUser() {
    if (!session?.token) return;
    setCreateUserBusy(true);
    try {
      const ok = window.confirm(
        `Criar utilizador ${String(newUserEmail || "").trim()} com role "${newUserRole}"? Será gerada uma password temporária.`
      );
      if (!ok) return;
      const r = await createAdminUser({
        authToken: session.token,
        firstName: newUserFirstName,
        lastName: newUserLastName,
        email: newUserEmail,
        role: newUserRole
      });
      setPwResetInfo({ ...r, at: new Date().toISOString() });
      setVerifyInfo(null);
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserEmail("");
      setNewUserRole("user");
      await refreshUsers();
      await refreshAdminStats();
      if (r?.userId) {
        setSelectedUserId(r.userId);
        await refreshUserDetail(r.userId);
        await refreshUserAudit(r.userId);
      }
    } catch (e) {
      alert("Erro a criar utilizador: " + (e && e.message ? e.message : String(e)));
    } finally {
      setCreateUserBusy(false);
    }
  }

  async function refreshUserDetail(userId) {
    if (!session?.token || !userId) return;
    setUserDetailLoading(true);
    try {
      const doc = await getAdminUser({ authToken: session.token, userId });
      setUserDetail(doc || null);
    } catch (e) {
      setUserDetail(null);
      alert("Erro a carregar detalhe: " + (e && e.message ? e.message : String(e)));
    } finally {
      setUserDetailLoading(false);
    }
  }

  async function refreshAttempts(userId) {
    if (!session?.token || !userId) return;
    setAttemptsLoading(true);
    try {
      const r = await getUserAttempts({ authToken: session.token, userId, limit: 120 });
      setAttempts((r && r.attempts) || []);
      setExpandedAttemptKey(null);
    } catch (e) {
      setAttempts([]);
    } finally {
      setAttemptsLoading(false);
    }
  }

  async function refreshUserAudit(userId) {
    if (!session?.token || !userId) return;
    setAuditLoading(true);
    try {
      const r = await getUserAuditLog({ authToken: session.token, userId, limit: 150 });
      setAuditEntries((r && r.entries) || []);
    } catch {
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  }

  async function refreshAdminStats() {
    if (!session?.token) return;
    setAdminStatsLoading(true);
    try {
      const s = await getAdminStats({ authToken: session.token });
      setAdminStats(s || null);
    } catch {
      setAdminStats(null);
    } finally {
      setAdminStatsLoading(false);
    }
  }

  useEffect(() => {
    if (!session?.token) return;
    if (tab !== "users") return;
    refreshUsers();
    refreshAdminStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, session?.token]);

  useEffect(() => {
    if (!session?.token) return;
    if (tab !== "deletions") return;
    refreshDeletions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, session?.token, delStatusFilter]);

  useEffect(() => {
    if (!selectedUserId) return;
    refreshUserDetail(selectedUserId);
    refreshAttempts(selectedUserId);
    refreshUserAudit(selectedUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  if (!session?.token) {
    return (
      <div className="container">
        <div className="title">Admin — Login</div>
        <Card className="panelCard" style={{ marginTop: 16 }}>
          <div className="row">
            <div className="grow">
              <div className="small">Email</div>
              <InputText value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="admin@example.com" style={{ width: "100%" }} />
            </div>
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <div className="grow">
              <div className="small">Password</div>
              <InputText
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••••"
                type="password"
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <Button label="Entrar" icon="pi pi-sign-in" className="p-button-sm p-button-primary" onClick={onLogin} />
          </div>
          {apiStatus ? <div className={"small " + (apiStatus.startsWith("LOGIN ERROR") ? "warn" : "")} style={{ marginTop: 8 }}>{apiStatus}</div> : null}
        </Card>
      </div>
    );
  }

  const filteredUsers = (() => {
    const q = String(usersQuery || "").trim().toLowerCase();
    const list = Array.isArray(users) ? users : [];
    if (!q) return list;
    return list.filter((u) => {
      const hay = `${u.firstName || ""} ${u.lastName || ""} ${u.email || ""} ${u.id || ""}`.toLowerCase();
      return hay.includes(q);
    });
  })();

  const filteredDeletionRequests = (() => {
    const q = String(delQuery || "").trim().toLowerCase();
    const list = Array.isArray(delRequests) ? delRequests : [];
    if (!q) return list;
    return list.filter((r) => {
      const hay = `${r.id || ""} ${r.userId || ""} ${r.userEmail || ""} ${r.status || ""} ${r.reason || ""}`.toLowerCase();
      return hay.includes(q);
    });
  })();

  const usersByStatus = bucketToMap(adminStats && adminStats.usersByStatus);
  const usersByRole = bucketToMap(adminStats && adminStats.usersByRole);
  const attemptsSummaryByTest = (() => {
    const map = new Map();
    for (const a of Array.isArray(attempts) ? attempts : []) {
      const key = String(a.testId || "");
      if (!key) continue;
      const curr = map.get(key) || {
        testId: key,
        titulation: a.titulation || "",
        testCode: a.testCode || "",
        totalQuestions: Number(a.totalQuestions || 0) || 0,
        attempts: 0,
        passed: 0,
        bestCorrect: 0,
        lastTs: null,
        modes: new Set()
      };
      curr.attempts += 1;
      if (a.passed) curr.passed += 1;
      curr.bestCorrect = Math.max(curr.bestCorrect, Number(a.correct || 0) || 0);
      const ts = a.ts || null;
      if (ts && (!curr.lastTs || String(ts) > String(curr.lastTs))) curr.lastTs = ts;
      curr.modes.add(a.mode || "assisted");
      if (!curr.testCode && a.testCode) curr.testCode = a.testCode;
      if (!curr.titulation && a.titulation) curr.titulation = a.titulation;
      const tq = Number(a.totalQuestions || (a.summary && a.summary.totalQuestions) || 0) || 0;
      if (!curr.totalQuestions && tq) curr.totalQuestions = tq;
      map.set(key, curr);
    }
    const out = [...map.values()].map((x) => ({ ...x, modes: [...x.modes] }));
    out.sort((a, b) => String(b.lastTs || "").localeCompare(String(a.lastTs || "")));
    return out;
  })();

  return (
    <div className="container">
      <OverlayPanel ref={actionsPanelRef} appendTo={document.body} style={{ minWidth: 240 }}>
        {(() => {
          const it = actionsTarget;
          if (!it) return <div className="small">—</div>;
          const busy = !!perTestApiBusyId || publishedSyncLoading;
          return (
            <div className="row" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
              {canExplain(it.titulation) ? (
                <Button
                  label="Gerar explicações"
                  icon="pi pi-sparkles"
                  className="p-button-sm p-button-secondary"
                  disabled={busy}
                  onClick={async () => {
                    actionsPanelRef.current?.hide?.();
                    await onExplain(it.id);
                  }}
                />
              ) : null}
              {Number(explCountByTestId[String(it.id)] || 0) > 0 ? (
                <Button
                  label="Limpar explicações (Mongo)"
                  icon="pi pi-trash"
                  className="p-button-sm p-button-warning"
                  disabled={busy}
                  onClick={async () => {
                    actionsPanelRef.current?.hide?.();
                    await onClearApiExplanations(it);
                  }}
                />
              ) : null}
              <Divider />
              <Button
                label="Eliminar"
                icon="pi pi-times"
                className="p-button-sm p-button-danger"
                disabled={busy}
                onClick={async () => {
                  actionsPanelRef.current?.hide?.();
                  await onRemove(it.id);
                }}
              />
            </div>
          );
        })()}
      </OverlayPanel>
      <div className="title">Admin</div>
      <div className="sub">Importação de exames e administração de utilizadores.</div>

      <Card className="panelCard" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="small">
            Sessão: <strong>{session.user?.email}</strong>
          </div>
          <div className="row">
            <Button
              label="Importar"
              icon="pi pi-upload"
              className={"p-button-sm " + (tab === "import" ? "p-button-primary" : "p-button-secondary")}
              onClick={() => setTab("import")}
            />
            <Button
              label="Utilizadores"
              icon="pi pi-users"
              className={"p-button-sm " + (tab === "users" ? "p-button-primary" : "p-button-secondary")}
              onClick={() => setTab("users")}
            />
            <Button
              label="Eliminações"
              icon="pi pi-trash"
              className={"p-button-sm " + (tab === "deletions" ? "p-button-primary" : "p-button-secondary")}
              onClick={() => setTab("deletions")}
            />
            <Button label="Sair" icon="pi pi-sign-out" className="p-button-sm p-button-secondary" onClick={onLogout} />
          </div>
        </div>
      </Card>

      {tab === "users" ? (
        <>
          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div className="row">
                <Tag value={adminStatsLoading ? "Stats: a carregar…" : "Stats"} />
                {adminStats ? (
                  <>
                    <Tag value={`Utilizadores: ${adminStats.totals?.users ?? "-"}`} />
                    <Tag value={`Tentativas: ${adminStats.totals?.attempts ?? "-"}`} />
                    <Tag value={`Tentativas (14d): ${adminStats.attemptsLast14d ?? "-"}`} />
                    <Tag value={`Ativos: ${usersByStatus.active || 0}`} />
                    <Tag value={`Suspensos: ${usersByStatus.suspended || 0}`} />
                    <Tag value={`Admins: ${usersByRole.admin || 0}`} />
                  </>
                ) : null}
              </div>
              <Button label="Recarregar" icon="pi pi-refresh" className="p-button-sm" onClick={() => (refreshUsers(), refreshAdminStats())} />
            </div>
          </Card>

          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div className="row">
                <Tag value={usersLoading ? "Utilizadores: a carregar…" : `Utilizadores: ${users.length}`} />
              </div>
              <div className="row">
                <div className="grow" style={{ minWidth: 260 }}>
                  <InputText value={usersQuery} onChange={(e) => setUsersQuery(e.target.value)} placeholder="Pesquisar por nome/email/id…" style={{ width: "100%" }} />
                </div>
              </div>
            </div>

            <Divider />
            <div className="small">
              <strong>Criar utilizador</strong>
            </div>
            <div className="row rowStackMobile" style={{ marginTop: 10, alignItems: "flex-end" }}>
              <div className="grow">
                <div className="small">Primeiro nome</div>
                <InputText value={newUserFirstName} onChange={(e) => setNewUserFirstName(e.target.value)} placeholder="Ex.: Maria" style={{ width: "100%" }} />
              </div>
              <div className="grow">
                <div className="small">Apelido</div>
                <InputText value={newUserLastName} onChange={(e) => setNewUserLastName(e.target.value)} placeholder="Ex.: Silva" style={{ width: "100%" }} />
              </div>
              <div className="grow">
                <div className="small">Email</div>
                <InputText value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="email@exemplo.com" style={{ width: "100%" }} />
              </div>
              <div style={{ minWidth: 180 }}>
                <div className="small">Role</div>
                <Dropdown
                  value={newUserRole}
                  options={[
                    { label: "User (frontend)", value: "user" },
                    { label: "Reviewer (Store validation)", value: "reviewer" },
                    { label: "Admin (admin panel)", value: "admin" },
                    { label: "SuperAdmin (full)", value: "superadmin" }
                  ]}
                  onChange={(e) => setNewUserRole(e.value)}
                  style={{ width: "100%" }}
                />
              </div>
              <Button
                label={createUserBusy ? "A criar…" : "Criar"}
                icon="pi pi-user-plus"
                className="p-button-sm p-button-primary"
                disabled={createUserBusy || usersLoading}
                onClick={onCreateUser}
              />
            </div>

            <div style={{ marginTop: 12, overflow: "auto" }}>
              <table className="simpleTable">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Plano</th>
                    <th>Criado</th>
                    <th>Último login</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {!filteredUsers.length ? (
                    <tr>
                      <td colSpan={8} className="small">
                        {usersLoading ? "A carregar…" : "Sem resultados."}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>{`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.id}</td>
                        <td className="small">{u.email}</td>
                        <td>{u.role}</td>
                        <td>{u.status}</td>
                        <td className="small">{u?.plan?.tier ? String(u.plan.tier) : "free"}</td>
                        <td className="small">{fmtDate(u.createdAt)}</td>
                        <td className="small">{u.lastLoginAt ? fmtDate(u.lastLoginAt) : "—"}</td>
                        <td>
                          <div className="row">
                            <Button label="Ver" icon="pi pi-search" className="p-button-sm p-button-secondary" onClick={() => setSelectedUserId(u.id)} />
                            {u.status === "pending" ? (
                              <Button
                                label="Validar"
                                icon="pi pi-check-circle"
                                className="p-button-sm p-button-success"
                                onClick={async () => {
                                  const ok = confirm(`Validar conta de ${u.email}?`);
                                  if (!ok) return;
                                  try {
                                    const r = await verifyUserAccount({ authToken: session.token, userId: u.id });
                                    setSelectedUserId(u.id);
                                    setVerifyInfo({ ...r, at: new Date().toISOString() });
                                    setPwResetInfo(null);
                                    await refreshUsers();
                                    await refreshAdminStats();
                                    await refreshUserDetail(u.id);
                                    await refreshUserAudit(u.id);
                                  } catch (e) {
                                    alert("Erro: " + (e && e.message ? e.message : String(e)));
                                  }
                                }}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {selectedUserId ? (
            <Card className="panelCard" style={{ marginTop: 16 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div className="row">
                  <Tag value={userDetailLoading ? "Utilizador: a carregar…" : "Utilizador"} />
                  <div className="small">{selectedUserId}</div>
                </div>
                <div className="row">
                  <Button
                    label="Fechar"
                    icon="pi pi-times"
                    className="p-button-sm p-button-secondary"
                    onClick={() => (
                      setSelectedUserId(null),
                      setUserDetail(null),
                      setAttempts([]),
                      setAuditEntries([]),
                      setPwResetInfo(null),
                      setVerifyInfo(null)
                    )}
                  />
                </div>
              </div>

              {userDetail ? (
                <>
                  <Divider />
                  <div className="row" style={{ alignItems: "flex-start" }}>
                    <div className="grow">
                      <div className="small">Nome</div>
                      <div>{`${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim()}</div>
                      <div className="small" style={{ marginTop: 8 }}>Email</div>
                      <div>{userDetail.email}</div>
                      <div className="row" style={{ marginTop: 10 }}>
                        <Tag value={`role: ${userDetail.role}`} />
                        <Tag value={`status: ${userDetail.status}`} />
                        <Tag value={`verificado: ${userDetail.verifiedAt ? "sim" : "não"}`} />
                        <Tag value={`plano: ${userDetail?.plan?.tier ? String(userDetail.plan.tier) : "free"}`} severity={userDetail?.plan?.tier === "pro" ? "success" : "secondary"} />
                      </div>
                      <div className="small" style={{ marginTop: 10 }}>Criado</div>
                      <div className="small">{fmtDate(userDetail.createdAt)}</div>
                      <div className="small" style={{ marginTop: 8 }}>Último login</div>
                      <div className="small">{userDetail.lastLoginAt ? fmtDate(userDetail.lastLoginAt) : "—"}</div>
                    </div>

                    <div className="grow">
                      <div className="small">Estatísticas (tentativas)</div>
                      <div className="row" style={{ marginTop: 8 }}>
                        <Tag value={`Total: ${userDetail.stats?.totalAttempts ?? 0}`} />
                        <Tag value={`Passou: ${userDetail.stats?.passed ?? 0}`} />
                        <Tag value={`Certas: ${userDetail.stats?.totalCorrect ?? 0}`} />
                        <Tag value={`Erradas: ${userDetail.stats?.totalIncorrect ?? 0}`} />
                      </div>

                      <Divider />
                      <div className="small">Ações</div>
                      <div className="row" style={{ marginTop: 8 }}>
                        <Button
                          label={userDetail?.plan?.tier === "pro" ? "Definir Free" : "Definir Pro"}
                          icon="pi pi-credit-card"
                          className={"p-button-sm " + (userDetail?.plan?.tier === "pro" ? "p-button-secondary" : "p-button-success")}
                          onClick={async () => {
                            try {
                              const next = userDetail?.plan?.tier === "pro" ? "free" : "pro";
                              await setUserPlan({ authToken: session.token, userId: userDetail.id, tier: next });
                              await refreshUsers();
                              await refreshUserDetail(userDetail.id);
                              await refreshAdminStats();
                              await refreshUserAudit(userDetail.id);
                            } catch (e) {
                              alert("Erro: " + (e && e.message ? e.message : String(e)));
                            }
                          }}
                        />
                        {userDetail.status === "pending" ? (
                          <Button
                            label="Validar conta"
                            icon="pi pi-check-circle"
                            className="p-button-sm p-button-success"
                            onClick={async () => {
                              const ok = confirm("Validar esta conta? O utilizador passa a ativo.");
                              if (!ok) return;
                              try {
                                const r = await verifyUserAccount({ authToken: session.token, userId: userDetail.id });
                                setVerifyInfo({ ...r, at: new Date().toISOString() });
                                setPwResetInfo(null);
                                await refreshUsers();
                                await refreshUserDetail(userDetail.id);
                                await refreshAdminStats();
                                await refreshUserAudit(userDetail.id);
                              } catch (e) {
                                alert("Erro: " + (e && e.message ? e.message : String(e)));
                              }
                            }}
                          />
                        ) : null}
                        <Button
                          label={userDetail.status === "suspended" ? "Reativar" : "Suspender"}
                          icon={userDetail.status === "suspended" ? "pi pi-check" : "pi pi-ban"}
                          className={"p-button-sm " + (userDetail.status === "suspended" ? "p-button-help" : "p-button-warning")}
                          onClick={async () => {
                            try {
                              const nextSuspended = userDetail.status !== "suspended";
                              await setUserSuspended({ authToken: session.token, userId: userDetail.id, suspended: nextSuspended });
                              await refreshUsers();
                              await refreshUserDetail(userDetail.id);
                              await refreshAdminStats();
                              await refreshUserAudit(userDetail.id);
                            } catch (e) {
                              alert("Erro: " + (e && e.message ? e.message : String(e)));
                            }
                          }}
                        />
                        <Button
                          label="Reset password"
                          icon="pi pi-key"
                          className="p-button-sm p-button-secondary"
                          onClick={async () => {
                            const ok = confirm("Gerar uma password temporária e terminar sessões deste utilizador?");
                            if (!ok) return;
                            try {
                              const r = await resetUserPassword({ authToken: session.token, userId: userDetail.id });
                              await refreshUserDetail(userDetail.id);
                              setVerifyInfo(null);
                              setPwResetInfo({ ...r, at: new Date().toISOString() });
                              await refreshUserAudit(userDetail.id);
                            } catch (e) {
                              alert("Erro: " + (e && e.message ? e.message : String(e)));
                            }
                          }}
                        />
                        <Button
                          label="Apagar"
                          icon="pi pi-trash"
                          className="p-button-sm p-button-danger"
                          onClick={async () => {
                            const ok = confirm("Apagar (soft delete) este utilizador? Isto remove a sessão e marca como deleted.");
                            if (!ok) return;
                            try {
                              await deleteUser({ authToken: session.token, userId: userDetail.id });
                              setSelectedUserId(null);
                              setUserDetail(null);
                              setVerifyInfo(null);
                              setPwResetInfo(null);
                              await refreshUsers();
                              await refreshAdminStats();
                            } catch (e) {
                              alert("Erro: " + (e && e.message ? e.message : String(e)));
                            }
                          }}
                        />
                      </div>

                      {verifyInfo && verifyInfo.userId === userDetail.id ? (
                        <div style={{ marginTop: 10 }}>
                          <div className="small">
                            Conta validada{verifyInfo.tempPassword ? " — password temporária (legacy)" : ""}
                          </div>
                          <div className="row" style={{ marginTop: 6 }}>
                            <Tag value={verifyInfo.emailed ? "Email enviado" : "Email não enviado"} />
                            <Tag value={fmtDate(verifyInfo.at)} />
                          </div>
                          {verifyInfo.tempPassword ? (
                            <div className="row" style={{ marginTop: 8 }}>
                              <InputText value={verifyInfo.tempPassword || ""} readOnly style={{ width: 320, maxWidth: "100%" }} />
                              <Button
                                label="Copiar"
                                icon="pi pi-copy"
                                className="p-button-sm p-button-help"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(String(verifyInfo.tempPassword || ""));
                                  } catch {
                                    alert(String(verifyInfo.tempPassword || ""));
                                  }
                                }}
                              />
                              <Button label="Limpar" icon="pi pi-times" className="p-button-sm p-button-secondary" onClick={() => setVerifyInfo(null)} />
                            </div>
                          ) : (
                            <div className="row" style={{ marginTop: 8 }}>
                              <Button label="OK" icon="pi pi-check" className="p-button-sm p-button-secondary" onClick={() => setVerifyInfo(null)} />
                            </div>
                          )}
                        </div>
                      ) : null}

                      {pwResetInfo && pwResetInfo.userId === userDetail.id ? (
                        <div style={{ marginTop: 10 }}>
                          <div className="small">Password temporária gerada</div>
                          <div className="row" style={{ marginTop: 6 }}>
                            <Tag value={pwResetInfo.emailed ? "Email enviado" : "Email não enviado"} />
                            <Tag value={fmtDate(pwResetInfo.at)} />
                          </div>
                          <div className="row" style={{ marginTop: 8 }}>
                            <InputText value={pwResetInfo.tempPassword || ""} readOnly style={{ width: 320, maxWidth: "100%" }} />
                            <Button
                              label="Copiar"
                              icon="pi pi-copy"
                              className="p-button-sm p-button-help"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(String(pwResetInfo.tempPassword || ""));
                                } catch {
                                  alert(String(pwResetInfo.tempPassword || ""));
                                }
                              }}
                            />
                            <Button
                              label="Limpar"
                              icon="pi pi-times"
                              className="p-button-sm p-button-secondary"
                              onClick={() => setPwResetInfo(null)}
                            />
                          </div>
                        </div>
                      ) : null}

                      <Divider />
                      <div className="row" style={{ justifyContent: "space-between" }}>
                        <div className="small">Tentativas recentes</div>
                        <Button
                          label={attemptsLoading ? "A carregar…" : "Recarregar"}
                          icon="pi pi-refresh"
                          className="p-button-sm p-button-secondary"
                          onClick={() => refreshAttempts(userDetail.id)}
                        />
                      </div>

                      <div style={{ marginTop: 10, overflow: "auto" }}>
                        <table className="simpleTable">
                          <thead>
                            <tr>
                              <th>Teste</th>
                              <th>Titulação</th>
                              <th className="num">Tentativas</th>
                              <th className="num">Passou</th>
                              <th className="num">Melhor</th>
                              <th>Modos</th>
                              <th>Última</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!attemptsSummaryByTest.length ? (
                              <tr>
                                <td colSpan={7} className="small">{attemptsLoading ? "A carregar…" : "Sem tentativas."}</td>
                              </tr>
                            ) : (
                              attemptsSummaryByTest.map((t) => (
                                <tr key={t.testId}>
                                  <td className="small">{t.testCode || t.testId}</td>
                                  <td className="small">{t.titulation || "—"}</td>
                                  <td className="num">{t.attempts}</td>
                                  <td className="num">{t.passed}</td>
                                  <td className="num">{t.totalQuestions ? `${t.bestCorrect}/${t.totalQuestions}` : t.bestCorrect}</td>
                                  <td className="small">{(t.modes || []).map(modeLabel).join(", ") || "—"}</td>
                                  <td className="small">{t.lastTs ? fmtDate(t.lastTs) : "—"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: 10, overflow: "auto" }}>
                        <table className="simpleTable">
                          <thead>
                            <tr>
                              <th>Quando</th>
                              <th>Titulação</th>
                              <th>Test</th>
                              <th>Modo</th>
                              <th>Resumo</th>
                              <th>Certas</th>
                              <th>Erradas</th>
                              <th>Brancas</th>
                              <th>Passou</th>
                              <th>Detalhe</th>
                              <th>BD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!attempts.length ? (
                              <tr>
                                <td colSpan={11} className="small">{attemptsLoading ? "A carregar…" : "Sem tentativas."}</td>
                              </tr>
                            ) : (
                              attempts.map((a, idx) => {
                                const rowKey = attemptRowKey(a, idx);
                                const sum = a.summary && typeof a.summary === "object" ? a.summary : null;
                                const score = sum?.score || (a.totalQuestions ? `${a.correct}/${a.totalQuestions}` : String(a.correct ?? ""));
                                const extra =
                                  sum && (sum.wrongDetailCount != null || sum.correctDetailCount != null)
                                    ? ` · err:${sum.wrongDetailCount ?? "—"} ok:${sum.correctDetailCount ?? "—"}`
                                    : "";
                                const open = expandedAttemptKey === rowKey;
                                return (
                                  <React.Fragment key={rowKey}>
                                    <tr>
                                      <td className="small">{fmtDate(a.ts)}</td>
                                      <td className="small">{a.titulation || "—"}</td>
                                      <td className="small">{a.testCode || a.testId || "—"}</td>
                                      <td className="small">{modeLabel(a.mode)}</td>
                                      <td className="small">
                                        {score}
                                        {extra}
                                      </td>
                                      <td>{Number(a.correct || 0)}</td>
                                      <td>{Number(a.incorrect || 0)}</td>
                                      <td>{Number(a.blank || 0)}</td>
                                      <td>{a.passed ? "Sim" : "Não"}</td>
                                      <td>
                                        <Button
                                          label={open ? "Ocultar" : "Ver"}
                                          icon={open ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                                          className="p-button-sm p-button-secondary"
                                          onClick={() => setExpandedAttemptKey(open ? null : rowKey)}
                                        />
                                      </td>
                                      <td className="small">
                                        <span className={a.detailsMeta && a.detailsMeta.stored === "partial" ? "warn" : ""}>{storageLabel(a.detailsMeta)}</span>
                                      </td>
                                    </tr>
                                    {open ? (
                                      <tr>
                                        <td colSpan={11}>
                                          <div className="small" style={{ marginBottom: 6 }}>
                                            <strong>Payload completo</strong> (summary + meta + details). Em produção isto pode ser grande.
                                          </div>
                                          <pre className="jsonPeek">
                                            {safeJsonStringify({
                                              summary: a.summary || null,
                                              detailsMeta: a.detailsMeta || null,
                                              top: {
                                                ts: a.ts,
                                                clientTs: a.clientTs,
                                                testId: a.testId,
                                                titulation: a.titulation,
                                                testCode: a.testCode,
                                                mode: a.mode,
                                                totalQuestions: a.totalQuestions,
                                                answered: a.answered,
                                                blank: a.blank,
                                                correct: a.correct,
                                                incorrect: a.incorrect,
                                                passed: a.passed,
                                                errorsByTopic: a.errorsByTopic
                                              },
                                              details: a.details || null
                                            })}
                                          </pre>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <Divider />
                  <div className="row" style={{ justifyContent: "space-between" }}>
                    <div className="small">
                      <strong>Histórico de operações (admin)</strong> — registado na base de dados
                    </div>
                    <Button
                      label={auditLoading ? "A carregar…" : "Recarregar"}
                      icon="pi pi-refresh"
                      className="p-button-sm p-button-secondary"
                      onClick={() => refreshUserAudit(userDetail.id)}
                    />
                  </div>
                  <div style={{ marginTop: 10, overflow: "auto" }}>
                    <table className="simpleTable">
                      <thead>
                        <tr>
                          <th>Quando</th>
                          <th>Operação</th>
                          <th>Quem</th>
                          <th>IP</th>
                          <th>Detalhes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!auditEntries.length ? (
                          <tr>
                            <td colSpan={5} className="small">{auditLoading ? "A carregar…" : "Sem operações registadas."}</td>
                          </tr>
                        ) : (
                          auditEntries.map((e) => (
                            <tr key={e.id || e.createdAt}>
                              <td className="small">{fmtDate(e.createdAt)}</td>
                              <td>{auditActionLabel(e.action)}</td>
                              <td className="small">{formatAuditActor(e.actor)}</td>
                              <td className="small">{e.ip || "—"}</td>
                              <td className="small" title={summarizeAuditDetails(e.details)}>{summarizeAuditDetails(e.details)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="small" style={{ marginTop: 10 }}>Sem detalhe.</div>
              )}
            </Card>
          ) : null}
        </>
      ) : tab === "deletions" ? (
        <>
          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <div className="row">
                <Tag value={delLoading ? "Pedidos: a carregar…" : `Pedidos: ${delRequests.length}`} />
              </div>
              <div className="row rowStackMobile" style={{ alignItems: "center" }}>
                <div style={{ minWidth: 220 }}>
                  <div className="small">Estado</div>
                  <Dropdown
                    value={delStatusFilter}
                    options={[
                      { label: "Pendente", value: "pending" },
                      { label: "Aprovado", value: "approved" },
                      { label: "Rejeitado", value: "rejected" },
                      { label: "Executado", value: "executed" },
                      { label: "Cancelado", value: "cancelled" },
                      { label: "Todos", value: "" }
                    ]}
                    onChange={(e) => setDelStatusFilter(e.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="grow" style={{ minWidth: 260 }}>
                  <div className="small">Pesquisar</div>
                  <InputText value={delQuery} onChange={(e) => setDelQuery(e.target.value)} placeholder="userId/email/id…" style={{ width: "100%" }} />
                </div>
                <Button label="Recarregar" icon="pi pi-refresh" className="p-button-sm" disabled={delLoading} onClick={refreshDeletions} />
              </div>
            </div>

            <Divider />

            <div style={{ overflow: "auto" }}>
              <table className="simpleTable" style={{ minWidth: "820px" }}>
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Estado</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Motivo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {!filteredDeletionRequests.length ? (
                    <tr>
                      <td colSpan={6} className="small">{delLoading ? "A carregar…" : "Sem pedidos."}</td>
                    </tr>
                  ) : (
                    filteredDeletionRequests.map((r) => {
                      const status = String(r.status || "");
                      const canApprove = status === "pending";
                      const canReject = status === "pending";
                      const canExecute = status === "approved";
                      const statusTag =
                        status === "pending" ? (
                          <Tag value="pendente" severity="warning" />
                        ) : status === "approved" ? (
                          <Tag value="aprovado" severity="info" />
                        ) : status === "rejected" ? (
                          <Tag value="rejeitado" severity="danger" />
                        ) : status === "executed" ? (
                          <Tag value="executado" severity="success" />
                        ) : status === "cancelled" ? (
                          <Tag value="cancelado" severity="secondary" />
                        ) : (
                          <Tag value={status || "—"} severity="secondary" />
                        );
                      return (
                        <tr key={r.id}>
                          <td className="small">{fmtDate(r.createdAt)}</td>
                          <td>{statusTag}</td>
                          <td className="small">{r.userId || "—"}</td>
                          <td className="small">{r.userEmail || "—"}</td>
                          <td className="small" title={r.reason || ""}>
                            {(r.reason || "—").length > 120 ? String(r.reason).slice(0, 117) + "…" : (r.reason || "—")}
                          </td>
                          <td>
                            <div className="row rowStackMobile">
                              <Button
                                label="Ver user"
                                icon="pi pi-user"
                                className="p-button-sm p-button-secondary"
                                onClick={() => (setTab("users"), setSelectedUserId(r.userId))}
                              />
                              <Button
                                label="Aprovar"
                                icon="pi pi-check"
                                className="p-button-sm p-button-help"
                                disabled={delLoading || !canApprove}
                                onClick={async () => {
                                  const ok = confirm("Aprovar pedido de eliminação?");
                                  if (!ok) return;
                                  try {
                                    await approveAccountDeletionRequest({ authToken: session.token, requestId: r.id, executeNow: false });
                                    await refreshDeletions();
                                    await refreshUserAudit(r.userId);
                                  } catch (e) {
                                    alert("Erro: " + (e && e.message ? e.message : String(e)));
                                  }
                                }}
                              />
                              <Button
                                label="Rejeitar"
                                icon="pi pi-times"
                                className="p-button-sm p-button-warning"
                                disabled={delLoading || !canReject}
                                onClick={async () => {
                                  const reason = prompt("Motivo da rejeição (opcional):", "");
                                  if (reason === null) return;
                                  try {
                                    await rejectAccountDeletionRequest({ authToken: session.token, requestId: r.id, reason });
                                    await refreshDeletions();
                                    await refreshUserAudit(r.userId);
                                  } catch (e) {
                                    alert("Erro: " + (e && e.message ? e.message : String(e)));
                                  }
                                }}
                              />
                              <Button
                                label="Executar"
                                icon="pi pi-trash"
                                className="p-button-sm p-button-danger"
                                disabled={delLoading || !canExecute}
                                onClick={async () => {
                                  const ok = confirm("EXECUTAR eliminação agora? Isto marca a conta como deleted e apaga tentativas/sessões.");
                                  if (!ok) return;
                                  try {
                                    await executeAccountDeletionRequest({ authToken: session.token, requestId: r.id });
                                    await refreshDeletions();
                                    await refreshUsers();
                                    await refreshAdminStats();
                                    await refreshUserAudit(r.userId);
                                  } catch (e) {
                                    alert("Erro: " + (e && e.message ? e.message : String(e)));
                                  }
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <>
          <div className="title" style={{ marginTop: 16 }}>Admin — Importar exames</div>
          <div className="sub">
            Sube el PDF de <strong>enunciados</strong> y el PDF de <strong>plantilla de respuestas</strong> para importar exámenes y
            guardarlos en la base de datos. Luego podrás resolverlos desde el frontend.
          </div>

          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="row">
              <div className="grow">
                <div className="small">Mes (ej. abril)</div>
                <InputText value={month} onChange={(e) => setMonth(e.target.value)} placeholder="abril" style={{ width: "100%" }} />
              </div>
              <div>
                <div className="small">Año (ej. 2026)</div>
                <InputNumber value={year} onValueChange={(e) => setYear(e.value)} min={2000} max={2100} />
              </div>
            </div>

            <div className="row" style={{ marginTop: 10 }}>
              <div className="grow">
                <div className="small">PDF enunciados</div>
                <input ref={pdfEnRef} type="file" accept="application/pdf" />
              </div>
              <div className="grow">
                <div className="small">PDF respuestas</div>
                <input ref={pdfAnRef} type="file" accept="application/pdf" />
              </div>
            </div>

            <div className="row" style={{ marginTop: 10 }}>
              <Tag value={status} />
              <div className="grow" />
              <Button label="Importar PDFs" icon="pi pi-upload" className="p-button-sm p-button-primary" onClick={onImportPdfs} />
              <Button label="Exportar JSON" icon="pi pi-copy" className="p-button-sm p-button-secondary" onClick={onExport} />
              <Button label="Publicar a API" icon="pi pi-cloud-upload" className="p-button-sm p-button-help" onClick={onPublishApi} />
              <Button label="Borrar todo" icon="pi pi-trash" className="p-button-sm p-button-danger" onClick={onWipe} />
            </div>
            <div className="row" style={{ marginTop: 10, alignItems: "flex-end" }}>
              <div className="grow" />
              <div>
                <div className="small">Reemplazar todo en Mongo</div>
                <Button
                  label={replaceAllOnPublish ? "Sí" : "No"}
                  className={"p-button-sm " + (replaceAllOnPublish ? "p-button-danger" : "p-button-secondary")}
                  onClick={() => setReplaceAllOnPublish((v) => !v)}
                />
              </div>
            </div>
            {apiStatus ? <div className={"small " + (apiStatus.startsWith("API OK") ? "" : "warn")} style={{ marginTop: 8 }}>{apiStatus}</div> : null}

            <div className="small" style={{ marginTop: 10 }}>
              Nota: esta página usa <code>pdf.js</code> en el navegador. Los tests se publican directamente en la API (MongoDB).
            </div>

            <Divider />
            <div className="small">
              <strong>Debug</strong>
            </div>
            <div className="logBox" style={{ marginTop: 8 }}>
              <InputTextarea value={logText} onChange={(e) => setLogText(e.target.value)} placeholder="Aquí aparecerá el log…" autoResize />
            </div>
          </Card>

          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div className="row">
                <Tag value={`Tests: ${items.length}`} />
                {publishedSyncLoading ? <Tag value="A sincronizar com a API…" severity="info" /> : null}
              </div>
              <Button
                label="Refrescar"
                icon="pi pi-refresh"
                className="p-button-sm"
                onClick={() => {
                  setItemsTick((t) => t + 1);
                  syncPublishedFromApi();
                }}
              />
            </div>
            {publishedSyncError ? (
              <div className="small warn" style={{ marginTop: 8 }}>
                Não foi possível obter o estado na API: {publishedSyncError}
              </div>
            ) : null}

            <div className="small" style={{ marginTop: 8, lineHeight: 1.45 }}>
              Com sessão iniciada, as explicações geradas gravam-se no <strong>MongoDB</strong> (nada no navegador). A
              coluna «Explic.» mostra o que já está na API; «Publicar a API» reprocessa os testes (e carrega explicações
              do Mongo) — útil sobretudo após migrações/ajustes de formato.
            </div>

            <div className="imported-tests-table-host" style={{ marginTop: 12 }}>
              <div className="row" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span className="p-input-icon-left" style={{ flex: "1 1 200px", minWidth: 160, maxWidth: "100%" }}>
                  <i className="pi pi-search" />
                  <InputText
                    value={importedTableGlobalFilter}
                    onChange={(e) => setImportedTableGlobalFilter(e.target.value)}
                    placeholder="Pesquisar (mês, ano, titulação, código do teste…)"
                    style={{ width: "100%" }}
                  />
                </span>
                <Button
                  type="button"
                  label="Limpar filtros"
                  icon="pi pi-filter-slash"
                  className="p-button-sm p-button-secondary"
                  onClick={clearImportedTableFilters}
                />
              </div>
              <DataTable
                value={importedCatalogRows}
                dataKey="familyKey"
                size="small"
                paginator
                rows={15}
                rowsPerPageOptions={[10, 15, 25, 50]}
                sortMode="multiple"
                removableSort
                filters={importedTableFilters}
                filterDisplay="menu"
                onFilter={(e) => setImportedTableFilters(e.filters)}
                globalFilter={importedTableGlobalFilter}
                globalFilterFields={["month", "yearDisplay", "titulation", "testCode", "activeLabel", "versionCountLabel", "explSearchText"]}
                globalFilterMatchMode="contains"
                emptyMessage={items.length ? "Nenhum teste corresponde aos filtros." : "Sem testes."}
                expandedRows={importedTableExpandedRows}
                onRowToggle={onImportedTableRowToggle}
                onRowExpand={onImportedTableRowExpand}
                rowExpansionTemplate={renderImportedTestExpansion}
                tableStyle={{ minWidth: "640px" }}
              >
                <Column expander style={{ width: "2.25rem" }} />
                <Column
                  field="month"
                  header="Mes"
                  sortable
                  filter
                  showFilterMenu
                  filterPlaceholder="Filtrar"
                  style={{ width: "5.5rem", maxWidth: "6rem" }}
                  body={(row) => <span className="small">{row.month ? row.month : "—"}</span>}
                />
                <Column
                  field="yearNum"
                  header="Año"
                  sortable
                  filter
                  showFilterMenu
                  dataType="numeric"
                  style={{ width: "4rem", maxWidth: "4.5rem" }}
                  body={(row) => <span className="small">{row.yearDisplay}</span>}
                  filterElement={(options) => (
                    <InputNumber
                      value={options.value}
                      onValueChange={(e) => options.filterApplyCallback(e.value)}
                      placeholder="Ej. 2026"
                      useGrouping={false}
                      className="p-column-filter"
                      inputClassName="p-column-filter"
                    />
                  )}
                />
                <Column
                  field="titulation"
                  header="Titulación"
                  sortable
                  filter
                  showFilterMenu
                  filterPlaceholder="Filtrar"
                  style={{ maxWidth: "11rem" }}
                  body={(row) => (
                    <span className="imported-tests-ellipsis small" title={row.titulation}>
                      {row.titulation || "—"}
                    </span>
                  )}
                />
                <Column
                  field="testCode"
                  header="Test"
                  sortable
                  filter
                  showFilterMenu
                  filterPlaceholder="Filtrar"
                  style={{ maxWidth: "9rem" }}
                  body={(row) => (
                    <span className="imported-tests-ellipsis small" title={row.testCode}>
                      {row.testCode || "—"}
                    </span>
                  )}
                />
                <Column
                  field="active"
                  header="Activo"
                  sortable
                  filter
                  showFilterMenu
                  style={{ maxWidth: "13rem" }}
                  body={(row) => {
                    const g = row._group;
                    const activeLabel = g.active ? `Sí (v${g.active?.version || 1})` : "No";
                    const activeStats = g.active ? testAttemptStatsById[String(g.active.id)] : null;
                    return (
                      <div className="imported-tests-main-active">
                        {g.active ? <Tag value={activeLabel} severity="success" /> : <Tag value={activeLabel} severity="secondary" />}
                        {g.active && activeStats ? (
                          <span
                            className="small"
                            style={{ lineHeight: 1.35, whiteSpace: "normal", wordBreak: "break-word" }}
                            title={`${activeStats.completedUsers} utilizadores completaram o teste ativo · ${activeStats.failedUsers} suspensos · ${activeStats.passedUsers} passaram`}
                          >
                            compl.: {activeStats.completedUsers} · susp.: {activeStats.failedUsers} · pass.:{" "}
                            {activeStats.passedUsers}
                          </span>
                        ) : null}
                      </div>
                    );
                  }}
                  filterElement={(options) => (
                    <Dropdown
                      value={options.value}
                      options={importedActiveFilterOptions}
                      onChange={(e) => options.filterApplyCallback(e.value)}
                      placeholder="Estado"
                      showClear
                      className="p-column-filter"
                      style={{ minWidth: "10rem" }}
                    />
                  )}
                />
                <Column
                  field="versionCount"
                  header="Ver."
                  sortable
                  filter
                  showFilterMenu
                  dataType="numeric"
                  style={{ width: "4.5rem", maxWidth: "5rem" }}
                  body={(row) => (
                    <span className="small">
                      <Tag value={String(row.versionCount)} />
                    </span>
                  )}
                  filterElement={(options) => (
                    <InputNumber
                      value={options.value}
                      onValueChange={(e) => options.filterApplyCallback(e.value)}
                      placeholder="Nº"
                      useGrouping={false}
                      min={0}
                      className="p-column-filter"
                      inputClassName="p-column-filter"
                    />
                  )}
                />
                <Column
                  header="Explic."
                  sortable
                  field="explLabel"
                  style={{ width: "6.5rem", maxWidth: "7rem" }}
                  body={(row) => {
                    const g = row._group;
                    const activeIt = g.active;
                    if (!activeIt) return <span className="small">—</span>;
                    if (!session?.token) {
                      return (
                        <span className="small" title="Inicie sessão para ver contagens de explicações na API.">
                          —
                        </span>
                      );
                    }
                    const nQ = (activeIt.questions || []).length;
                    const nE = Number(explCountByTestId[String(activeIt.id)] || 0);
                    const full = nQ > 0 && nE >= nQ;
                    const partial = nE > 0 && !full;
                    return (
                      <span
                        className="small"
                        title="Explicações guardadas na API (Mongo) para esta versão do teste."
                      >
                        {full ? (
                          <Tag value={`${nE}/${nQ}`} severity="success" />
                        ) : partial ? (
                          <Tag value={`${nE}/${nQ}`} severity="warning" />
                        ) : (
                          <Tag value={nQ ? `0/${nQ}` : "0"} severity="secondary" />
                        )}
                      </span>
                    );
                  }}
                />
                <Column
                  header="Ver"
                  style={{ width: "3.25rem" }}
                  body={(row) => {
                    const fk = row.familyKey;
                    const open = importedTableExpandedRows && importedTableExpandedRows[fk];
                    return (
                      <Button
                        icon={open ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                        className="p-button-sm p-button-secondary"
                        type="button"
                        aria-label={open ? "Ocultar versões" : "Ver versões"}
                        tooltip={open ? "Ocultar" : "Ver versões"}
                        tooltipOptions={{ position: "top" }}
                        onClick={async () => {
                          if (open) {
                            setImportedTableExpandedRows(null);
                            return;
                          }
                          await ensureAttemptStats(row._group.versions.map((x) => x.id));
                          setImportedTableExpandedRows({ [fk]: true });
                        }}
                      />
                    );
                  }}
                />
              </DataTable>
            </div>
          </Card>

          <Card className="panelCard" style={{ marginTop: 16 }}>
            <div className="small">
              <strong>Importar desde JSON (pega aquí)</strong>
            </div>
            <div style={{ marginTop: 8 }}>
              <InputTextarea value={jsonIn} onChange={(e) => setJsonIn(e.target.value)} placeholder='[ { "id": "...", ... } ]' autoResize />
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <Button label="Importar JSON" icon="pi pi-download" className="p-button-sm p-button-primary" onClick={onImportJson} />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

